import { ApiError, ForbiddenError, UnauthorizedError } from "./errors";
import type {
  AdminUser,
  AdminUserInput,
  AdminUserPatch,
  AuditEvent,
  AuditFilters,
  Block,
  BlockInput,
  BlockPatch,
  Device,
  ImportCommitResult,
  ImportPreview,
  Invitation,
  Occupancy,
  OccupancyInput,
  OccupancyPatch,
  Person,
  PersonInput,
  PersonPatch,
  PortalApi,
  SessionResponse,
  TenantProfile,
  TenantProfilePatch,
  TenantRole,
  Unit,
  UnitInput,
  UnitPatch,
} from "./types";

export type HttpApiOptions = {
  baseUrl: string;
  getToken: () => Promise<string>;
  // Chamado em 401 para reiniciar o login. O erro ainda é propagado.
  onUnauthorized: () => void;
};

const BASE_PATH = "/api/v1/tenant";

export function createHttpApi(options: HttpApiOptions): PortalApi {
  const baseUrl = options.baseUrl.replace(/\/$/, "");

  async function request<T>(
    tenantId: string | null,
    path: string,
    init: RequestInit = {},
  ): Promise<T> {
    const token = await options.getToken();
    const headers: Record<string, string> = {
      Authorization: `Bearer ${token}`,
      ...(tenantId ? { "X-AVA-Tenant-ID": tenantId } : {}),
      ...(init.body instanceof FormData ? {} : { "Content-Type": "application/json" }),
      ...(init.headers as Record<string, string> | undefined),
    };
    const response = await fetch(`${baseUrl}${BASE_PATH}${path}`, { ...init, headers });
    if (response.status === 401) {
      options.onUnauthorized();
      throw new UnauthorizedError();
    }
    if (response.status === 403) {
      throw new ForbiddenError();
    }
    if (!response.ok) {
      const body = (await response.json().catch(() => null)) as { error?: string } | null;
      throw new ApiError(response.status, body?.error || `Erro ${response.status}`);
    }
    if (response.status === 204) return undefined as T;
    return (await response.json()) as T;
  }

  function json(method: string, body: unknown): RequestInit {
    return { method, body: JSON.stringify(body) };
  }

  async function list<T>(tenantId: string, path: string): Promise<T[]> {
    const data = await request<{ items: T[] }>(tenantId, path);
    return data.items;
  }

  return {
    getSession: () => request<SessionResponse>(null, "/session"),
    redeemOwnerInvitation: (inviteToken, pin) =>
      request<{ tenant_id: string; role: TenantRole }>(
        null,
        "/owner-invitations/redeem",
        json("POST", { invite_token: inviteToken, pin }),
      ),

    getProfile: (t) => request<TenantProfile>(t, "/profile"),
    updateProfile: (t, patch: TenantProfilePatch) =>
      request<TenantProfile>(t, "/profile", json("PATCH", patch)),

    listAdminUsers: (t) => list<AdminUser>(t, "/admin-users"),
    inviteAdminUser: (t, input: AdminUserInput) =>
      request<AdminUser>(t, "/admin-users", json("POST", input)),
    updateAdminUser: (t, id, patch: AdminUserPatch) =>
      request<AdminUser>(t, `/admin-users/${id}`, json("PATCH", patch)),
    removeAdminUser: (t, id) => request<void>(t, `/admin-users/${id}`, { method: "DELETE" }),

    listBlocks: (t) => list<Block>(t, "/blocks"),
    createBlock: (t, input: BlockInput) => request<Block>(t, "/blocks", json("POST", input)),
    updateBlock: (t, id, patch: BlockPatch) =>
      request<Block>(t, `/blocks/${id}`, json("PATCH", patch)),

    listUnits: (t) => list<Unit>(t, "/units"),
    createUnit: (t, input: UnitInput) => request<Unit>(t, "/units", json("POST", input)),
    updateUnit: (t, id, patch: UnitPatch) =>
      request<Unit>(t, `/units/${id}`, json("PATCH", patch)),

    listPeople: (t) => list<Person>(t, "/people"),
    createPerson: (t, input: PersonInput) => request<Person>(t, "/people", json("POST", input)),
    updatePerson: (t, id, patch: PersonPatch) =>
      request<Person>(t, `/people/${id}`, json("PATCH", patch)),

    listOccupancies: (t) => list<Occupancy>(t, "/occupancies"),
    createOccupancy: (t, input: OccupancyInput) =>
      request<Occupancy>(t, "/occupancies", json("POST", input)),
    updateOccupancy: (t, id, patch: OccupancyPatch) =>
      request<Occupancy>(t, `/occupancies/${id}`, json("PATCH", patch)),
    deleteOccupancy: (t, id) => request<void>(t, `/occupancies/${id}`, { method: "DELETE" }),

    listInvitations: (t) => list<Invitation>(t, "/invitations"),
    createInvitation: (t, occupancyId) =>
      request<Invitation>(t, `/occupancies/${occupancyId}/invitations`, json("POST", {})),
    resendInvitation: (t, id) =>
      request<Invitation>(t, `/invitations/${id}/resend`, json("POST", {})),
    revokeInvitation: (t, id) =>
      request<Invitation>(t, `/invitations/${id}/revoke`, json("POST", {})),

    listDevices: (t) => list<Device>(t, "/devices"),
    revokeDevice: (t, id) => request<void>(t, `/devices/${id}`, { method: "DELETE" }),

    previewImport: (t, file) => {
      const body = new FormData();
      body.append("file", file);
      return request<ImportPreview>(t, "/imports/preview", { method: "POST", body });
    },
    commitImport: (t, importId) =>
      request<ImportCommitResult>(t, "/imports/commit", json("POST", { import_id: importId })),

    listAuditEvents: (t, filters?: AuditFilters) => {
      const params = new URLSearchParams();
      if (filters?.action) params.set("action", filters.action);
      if (filters?.entity_type) params.set("entity_type", filters.entity_type);
      const suffix = params.size ? `?${params.toString()}` : "";
      return list<AuditEvent>(t, `/audit-events${suffix}`);
    },
  };
}
